// Payout Hold Banner
// ────────────────────────────────────────────────────────────────────
// Persistent notice shown across the whole business portal while FASH is
// withholding a store's proceeds pending their tax paperwork.
//
// It is not dismissible — only collapsible for the current page view —
// because the store cannot be paid until they act on it. The verdict and
// the dollar figure both come from the server; nothing here is computed
// client-side.

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { usePayoutCompliance } from '../../hooks/usePayoutCompliance';
import { cn } from '../../lib/utils';

const TAX_SETTINGS_LINK = '/business/settings?section=legal';

function formatMoney(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function daysSince(date: Date | null): number {
  if (!date) return 0;
  return Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
}

const amberTone = {
  border: 'border-amber-200',
  bg: 'bg-amber-50',
  iconBg: 'bg-amber-100',
  icon: 'text-amber-700',
  headline: 'text-amber-900',
  body: 'text-amber-800',
  muted: 'text-amber-700',
  dot: 'bg-amber-500',
  button: 'bg-amber-600 hover:bg-amber-700',
  toggle: 'text-amber-700 hover:bg-amber-100',
};

const redTone = {
  border: 'border-red-200',
  bg: 'bg-red-50',
  iconBg: 'bg-red-100',
  icon: 'text-red-700',
  headline: 'text-red-900',
  body: 'text-red-800',
  muted: 'text-red-700',
  dot: 'bg-red-500',
  button: 'bg-red-600 hover:bg-red-700',
  toggle: 'text-red-700 hover:bg-red-100',
};

export function PayoutHoldBanner() {
  const {
    loading,
    payoutEligible,
    taxComplete,
    status,
    missing,
    stripeBlockers,
    heldCount,
    heldTotalCents,
    oldestHeldAt,
    needsStripeConnection,
    inGracePeriod,
    graceEndsAt,
    graceDaysLeft,
  } = usePayoutCompliance();

  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  if (loading || payoutEligible) return null;

  // A store with no Stripe account at all already sees the "connect Stripe"
  // prompt in onboarding and settings. Stacking a second, contradictory
  // warning on top of it just reads as noise.
  if (needsStripeConnection) return null;

  // Already on the page that fixes it, with the section open — the inline
  // form is the better affordance there.
  const onTaxSettings =
    location.pathname === '/business/settings' &&
    new URLSearchParams(location.search).get('section') === 'legal';

  const heldDays = daysSince(oldestHeldAt);
  const blockers = [...missing.map((m) => m.label), ...stripeBlockers];

  // Two distinct states wear two distinct colours: a store still inside its
  // window is being warned, and telling it "your payouts are on hold" when
  // they are not would be false.
  const tone = inGracePeriod ? amberTone : redTone;

  let headline: string;
  if (inGracePeriod) {
    headline = graceDaysLeft !== null
      ? `Add your tax information within ${graceDaysLeft} day${graceDaysLeft === 1 ? '' : 's'}`
      : 'Your tax information is incomplete';
  } else if (!taxComplete) {
    headline = status === 'needs_recertification'
      ? 'Re-sign your W-9 to resume payouts'
      : 'Your payouts are on hold';
  } else {
    headline = 'Stripe needs more information before payouts can resume';
  }

  let summary: string;
  if (inGracePeriod) {
    summary = graceEndsAt
      ? `Your payouts are arriving normally, but FASH must hold them from ` +
        `${graceEndsAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} ` +
        `until your tax information is on file.`
      : 'Complete your tax information to keep your payouts arriving on schedule.';
  } else if (heldCount > 0) {
    summary = `${formatMoney(heldTotalCents)} from ${heldCount} order${heldCount === 1 ? '' : 's'} is being held${
      heldDays > 0 ? `, the oldest for ${heldDays} day${heldDays === 1 ? '' : 's'}` : ''
    }.`;
  } else {
    summary = 'Complete your tax information now and every order will pay out on the normal schedule.';
  }

  return (
    <div className={cn('sticky top-0 z-40 border-b', tone.border, tone.bg)}>
      <div className="mx-auto max-w-5xl px-4 py-3">
        <div className="flex items-start gap-3">
          <div className={cn(
            'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
            tone.iconBg,
          )}>
            <AlertTriangle className={cn('h-4 w-4', tone.icon)} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn('text-sm font-semibold', tone.headline)}>{headline}</p>
                <p className={cn('mt-0.5 text-xs leading-relaxed', tone.body)}>{summary}</p>
              </div>

              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                aria-expanded={!collapsed}
                aria-label={collapsed ? 'Show what is needed' : 'Hide details'}
                className={cn(
                  'flex-shrink-0 rounded-lg p-1.5 transition-colors',
                  tone.toggle,
                )}
              >
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
            </div>

            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                >
                  <ul className="mt-3 space-y-1.5">
                    {blockers.map((label) => (
                      <li key={label} className={cn('flex items-start gap-2 text-xs', tone.headline)}>
                        <span aria-hidden className={cn('mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full', tone.dot)} />
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>

                  <p className={cn('mt-3 text-xs', tone.muted)}>
                    {inGracePeriod
                      ? 'Finish before the deadline and nothing about your payouts changes.'
                      : 'Nothing is forfeited. Held funds are released automatically once your information is complete.'}
                  </p>

                  {!onTaxSettings && (
                    <Link
                      to={TAX_SETTINGS_LINK}
                      className={cn(
                        'mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2',
                        'text-xs font-semibold text-white transition-colors',
                        tone.button,
                      )}
                    >
                      Complete tax information
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
